/**
 * Banner markup builder — the storefront's copy of the retailer portal's
 * `GrapesEditor/bannerMarkup.js`, the source of truth for what a banner looks
 * like. The pinned Main banner and every added banner render through it,
 * so they cannot drift from each other or from the designer.
 *
 * Dynamic values (storefront color, its tint, a photo URL) go through inline
 * `style`, never a class name — Tailwind only emits literal class strings.
 */

import { escapeHtml } from '../../utilities/helper';
import { lightToneOf } from '../../shared/theme/storefrontTheme';

/* The ShopDot orange: the fallback storefront color and the one hover / focus
   accent the storefront uses for text. It stays a literal hex in the CSS strings
   that consume it — a custom property would be dropped by GrapesJS's style
   manager when the designer re-parses this markup. */
export const DEFAULT_THEME = '#E15439';
const INK = '#2C2F32';
const MUTED = '#565E64';

const toRgb = (hex) => {
  const clean = String(hex || '').trim().replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
};

const normalizeTheme = (hex) => (toRgb(hex) ? hex : DEFAULT_THEME);

/** The Light tint: the storefront color at `amount` over white. Derived on every
 *  render from the one stored color — nothing extra is saved. */
export const mixOverWhite = (hex, amount) => {
  const rgb = toRgb(hex) || toRgb(DEFAULT_THEME);
  const mix = (channel) => Math.round(channel * amount + 255 * (1 - amount));
  return `rgb(${mix(rgb.r)}, ${mix(rgb.g)}, ${mix(rgb.b)})`;
};

/* ---- Text on a colored surface: light swatches have a darker tone (lightToneOf), every other color is dark ---- */
const NEAR_BLACK = '#241f1d';

const toneOf = lightToneOf;

const isDarkColor = (bg) => !toneOf(bg);

const readableOn = (bg) => (isDarkColor(bg) ? '#FFFFFF' : NEAR_BLACK);

/* A pale store color has no visible boundary on white (or its own band), so a
   button filled with it takes a hairline in the color's darker tone. */
const buttonEdgeOf = (bg) => toneOf(bg) || 'transparent';

/** The store color used as TEXT on white or a light surface — a pale color
    would vanish there, so it uses its darker tone. */
export const accentTextOf = (bg) => toneOf(bg) || bg;

const SVG_SHIELD =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:14px;height:14px;flex:none"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>';

const SVG_ARROW =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:15px;height:15px;flex:none"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>';

const SVG_CHECK =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="width:15px;height:15px;flex:none"><polyline points="20 6 9 17 4 12"/></svg>';

const TEXT_ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' };
/* A button row and a badge row are flex containers, so text-align does nothing
   to them — they need their own justification. */
const JUSTIFY = { left: 'justify-start', center: 'justify-center', right: 'justify-end' };
const ITEMS = { left: 'items-start', center: 'items-center', right: 'items-end' };

const alignOf = (value) => (TEXT_ALIGN[value] ? value : 'center');
/** Split offers only two positions: which side of the row the copy sits on. */
const sideOf = (value) => (value === 'right' ? 'right' : 'left');

const href = (link) => {
  const clean = String(link || '').trim();
  return clean ? escapeHtml(clean) : '#';
};

/**
 * Every banner renders onto one of three surfaces, each needing explicit colors
 * because nothing here can safely inherit.
 *
 *  - `brand` — the band IS the storefront color, so the primary button inverts
 *    to white and the secondary becomes a white outline.
 *  - `photo` — a photo behind a dark scrim; the primary keeps the storefront
 *    color and the secondary goes solid white.
 *  - `light` — the 10% tint or a plain light panel. Ordinary colors.
 *
 * A button filled with the storefront color takes its text from the color's
 * group (`readableOn`), never a fixed white. A `brand` band in a pale color is a
 * light surface (dark text).
 */
const surfaceOf = (kind, theme) => {
  const filled = `background:${theme};color:${readableOn(theme)};border:1px solid ${buttonEdgeOf(theme)}`;
  const accentText = accentTextOf(theme);
  if (kind === 'brand' && isDarkColor(theme)) {
    return {
      onDark: true,
      title: '#FFFFFF',
      lead: 'rgba(255,255,255,.92)',
      eyebrow: 'color:#fff;background:rgba(255,255,255,.18);border:1px solid rgba(255,255,255,.35)',
      // Dark text, not the store color — the reference's `.hb-on-dark .sf-btn-primary`.
      primary: `background:#FFFFFF;color:${INK};border:1px solid transparent`,
      secondary: 'background:transparent;color:#fff;border:1px solid rgba(255,255,255,.55)',
      trustText: 'rgba(255,255,255,.9)',
      trustCheck: '#FFFFFF',
    };
  }
  if (kind === 'photo') {
    return {
      onDark: true,
      title: '#FFFFFF',
      lead: 'rgba(255,255,255,.9)',
      eyebrow: 'color:#fff;background:rgba(255,255,255,.18);border:1px solid rgba(255,255,255,.35)',
      primary: filled,
      secondary: `background:#FFFFFF;color:${INK};border:1px solid transparent;font-weight:600`,
      trustText: 'rgba(255,255,255,.9)',
      trustCheck: '#FFFFFF',
    };
  }
  return {
    onDark: false,
    title: INK,
    lead: MUTED,
    eyebrow: `color:${accentText};background:${mixOverWhite(theme, 0.12)};border:1px solid ${mixOverWhite(theme, 0.3)}`,
    primary: filled,
    secondary: `background:#FFFFFF;color:${INK};border:1px solid #CDD1D4`,
    trustText: MUTED,
    trustCheck: accentText,
  };
};

/**
 * A copy field, or '' when there is nothing real in it.
 *
 * "[Add Button Text]" and "[Enter Title Here]" are seed values from the
 * block-templates API's `defaultContent`, so a partner who never opened the
 * field would publish a literal "[Add Button Text]" button. Treating the
 * bracketed shape as empty at render time heals that without touching saved
 * data.
 */
const PLACEHOLDER = /^\[.*\]$/;
const STOREFRONT_DARK = NEAR_BLACK;

export const bannerFillsFor = (themeColor, isMain = false) => {
  if (isMain) return ['block'];
  const theme = normalizeTheme(themeColor);
  if (theme.toLowerCase() === STOREFRONT_DARK) return ['block', 'tint'];
  if (!isDarkColor(theme)) return ['block', 'dark'];
  return ['block', 'tint', 'dark'];
};

export const resolveBannerFill = (fill, themeColor, isMain = false) => {
  const f = String(fill || 'block');
  return bannerFillsFor(themeColor, isMain).includes(f) ? f : 'block';
};

const fillSurface = (fill, theme) => {
  if (fill === 'dark') return { surface: surfaceOf('photo', theme), background: STOREFRONT_DARK };
  if (fill === 'tint') return { surface: surfaceOf('light', theme), background: mixOverWhite(theme, 0.1) };
  return { surface: surfaceOf('brand', theme), background: theme };
};

export const copyOf = (value) => {
  const text = String(value ?? '').trim();
  return PLACEHOLDER.test(text) ? '' : text;
};

/* Vertical rhythm: the eyebrow and headline sit tight together, the button row
   gets real air above it, and the badges clear the buttons. */
const eyebrowHtml = (raw, surface, gap) => {
  const text = copyOf(raw);
  if (!text) return '';
  return (
    `<span class="hero-pill inline-flex items-center gap-[7px] rounded-full px-[13px] py-[6px] text-[12.5px] font-semibold w-fit ${gap}"` +
    ` style="${surface.eyebrow}">${SVG_SHIELD}<span>${escapeHtml(text)}</span></span>`
  );
};

/* A banner's headline is a heading, so it carries the Style tab's heading font
   explicitly — style changes headings only. */
const headlineHtml = (raw, { size, color, gap, font, fullWidth = false }) => {
  const text = copyOf(raw);
  if (!text) return '';
  const span = fullWidth && !/\s/.test(text)
    ? ' w-max max-w-full'
    : '';
  return `<h1 class="hero-title whitespace-pre-line font-semibold tracking-[-0.02em] leading-[1.06] !mt-0 [overflow-wrap:anywhere] [text-wrap:balance]${span} ${gap}" style="font-size:${size};color:${color}${font ? `;font-family:${font}` : ''}">${escapeHtml(text)}</h1>`;
};

const leadHtml = (raw, { color, maxWidth, size, align }) => {
  const text = copyOf(raw);
  if (!text) return '';
  const centering =
    align === 'center'
      ? 'margin-left:auto;margin-right:auto'
      : align === 'right'
        ? 'margin-left:auto'
        : '';
  return `<p class="hero-lead whitespace-pre-line !mt-0 !mb-0 [overflow-wrap:anywhere] [text-wrap:pretty]" style="font-size:${size};color:${color};max-width:${maxWidth};${centering}">${escapeHtml(text)}</p>`;
};

/**
 * A button's label, or '' when the button should not render. A button needs
 * both a label and a link: a label alone goes nowhere, and older banners were
 * seeded with a "Learn More" label and no link.
 */
const buttonLabelOf = (text, link) =>
  String(link ?? '').trim() ? copyOf(text) : '';

/**
 * Button row. Each button renders when it has a label and a link — there is no
 * separate "include button" switch in the designer.
 */
const buttonsHtml = (banner, surface, align) => {
  const primaryLabel = buttonLabelOf(banner.buttonText, banner.buttonLink);
  const secondaryLabel = buttonLabelOf(banner.secondaryButtonText, banner.secondaryButtonLink);
  if (!primaryLabel && !secondaryLabel) return '';

  const base =
    'no-underline inline-flex items-center gap-[8px] whitespace-nowrap rounded-[8px] px-[20px] py-[12px] text-[14.5px] font-medium leading-none';

  const primary = primaryLabel
    ? `<a href="${href(banner.buttonLink)}" target="_blank" rel="noopener noreferrer" class="hero-btn-primary ${base}"` +
    ` style="${surface.primary}"><span>${escapeHtml(primaryLabel)}</span>${SVG_ARROW}</a>`
    : '';

  const secondary = secondaryLabel
    ? `<a href="${href(banner.secondaryButtonLink)}" target="_blank" rel="noopener noreferrer" class="hero-btn-secondary ${base}"` +
    ` style="${surface.secondary}"><span>${escapeHtml(secondaryLabel)}</span></a>`
    : '';

  return `<div class="hero-cta flex flex-wrap items-center gap-[12px] mt-[26px] ${JUSTIFY[align]}">${primary}${secondary}</div>`;
};

/** Trust badges — short reassurances in a wrapping row under the buttons. */
const trustHtml = (badges, surface, align) => {
  const items = (Array.isArray(badges) ? badges : [])
    .map((badge) => String(badge || '').trim())
    .filter(Boolean);
  if (!items.length) return '';

  const cells = items
    .map(
      (badge) =>
        '<span class="inline-flex items-center gap-[7px]">' +
        `<span style="color:${surface.trustCheck};display:inline-flex">${SVG_CHECK}</span>` +
        `<span>${escapeHtml(badge)}</span></span>`
    )
    .join('');

  return `<div class="hero-trust flex flex-wrap items-center gap-x-[18px] gap-y-[8px] text-[13.5px] mt-[20px] ${JUSTIFY[align]}" style="color:${surface.trustText}">${cells}</div>`;
};

const join = (parts) => parts.filter(Boolean).join('');

/* Phones get `mobileBannerImage` when there is one, else `bannerImage`. */
const MOBILE_MEDIA = '(max-width: 767px)';

/* The photo layer, shared by Split and Image. With a mobile image it is a
   <picture>; `contents` keeps the wrapper out of layout, so the <img> sizes
   exactly as it does without one. */
const photoHtml = (banner, extraClass) => {
  const src = String(banner.bannerImage || '').trim();
  const mobileSrc = String(banner.mobileBannerImage || '').trim();
  if (!src && !mobileSrc) return '';
  const img = `<img src="${escapeHtml(src || mobileSrc)}" alt="" loading="lazy" class="hero-img ${extraClass}" />`;
  if (!mobileSrc || !src) return img;
  return `<picture class="hero-picture contents"><source media="${MOBILE_MEDIA}" srcset="${escapeHtml(mobileSrc)}" />${img}</picture>`;
};

/**
 * Split — a width-capped copy column beside a photo that fills the row.
 *
 * Text alignment picks which SIDE the copy sits on, not how the words line up:
 * the column has its own edge to read from, so Left/Right are the only two
 * positions that mean anything.
 */
const splitBanner = (banner, theme, font, fill) => {
  const { surface, background: columnBg } = fillSurface(fill, theme);
  const side = sideOf(banner.contentAlignHorizontal);

  const body = join([
    eyebrowHtml(banner.eyebrow, surface, 'mb-[12px]'),
    headlineHtml(banner.title, {
      size: 'clamp(24px,2.3vw,31px)',
      color: surface.title,
      gap: '!mb-[10px]',
      font,
    }),
    leadHtml(banner.subtitle, {
      size: '15.5px',
      color: surface.lead,
      maxWidth: 'none',
      align: 'left',
    }),
    buttonsHtml(banner, surface, 'left'),
    trustHtml(banner.trustBadges, surface, 'left'),
  ]);

  const textCol = `<div class="hero-content flex flex-col justify-center px-[24px] py-[32px] md:px-[52px] md:py-[48px] text-left" style="background:${columnBg}">${body}</div>`;
  const photoCol = `<div class="hero-photo relative overflow-hidden bg-[#F0F2F3] aspect-[17/9] md:aspect-auto">${photoHtml(
    banner,
    'absolute inset-0 w-full h-full object-cover'
  )}</div>`;

  // Written out in full rather than composed, so Tailwind can see both track
  // definitions as literal strings. The main branch's banner ratio: the whole
  // row is 16:5 from md up (it grows if the copy is taller) and the photo fills
  // its column; stacked on a phone, the photo alone is 17:9.
  const grid =
    side === 'right'
      ? `<div class="grid grid-cols-1 md:grid-cols-[60fr_40fr] md:aspect-[16/5]">${photoCol}${textCol}</div>`
      : `<div class="grid grid-cols-1 md:grid-cols-[40fr_60fr] md:aspect-[16/5]">${textCol}${photoCol}</div>`;

  return `<div class="relative min-w-0 overflow-hidden">
  <div class="main-banner-div hero-split relative overflow-hidden">
    ${grid}
  </div>
</div>`;
};

/** Image — the headline sits over the photo behind a dark scrim. No copy, no
    scrim: a banner with its text baked into the image shows it untinted. The
    photo band keeps the main branch's ratio: 17:9 on a phone, 16:5 from md up.
    It grows rather than clips when the copy is taller: aspect-ratio's automatic
    minimum height is the content, but only while the band is not
    `overflow-hidden` — the outer wrapper does the clipping instead. */
const imageBanner = (banner, theme, font) => {
  const surface = surfaceOf('photo', theme);
  const align = alignOf(banner.contentAlignHorizontal);

  const body = join([
    eyebrowHtml(banner.eyebrow, surface, 'mb-[18px]'),
    headlineHtml(banner.title, {
      size: 'clamp(28px,4.4vw,50px)',
      color: surface.title,
      gap: '!mb-[14px]',
      font,
      fullWidth: true,
    }),
    leadHtml(banner.subtitle, {
      size: '17px',
      color: surface.lead,
      maxWidth: '480px',
      align,
    }),
    buttonsHtml(banner, surface, align),
    trustHtml(banner.trustBadges, surface, align),
  ]);

  return `<div class="relative min-w-0 overflow-hidden">
  <div class="hero-img-loader absolute inset-0 flex items-center justify-center bg-white z-20">
    <svg width="40" height="40" viewBox="0 0 40 40">
      <circle cx="20" cy="20" r="18" stroke="#999" stroke-width="4" fill="none">
        <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="1s" repeatCount="indefinite" />
      </circle>
    </svg>
  </div>
  <div class="main-banner-div hero-image relative flex flex-col justify-center ${ITEMS[align]} aspect-[17/9] md:aspect-[16/5] px-[24px] py-[32px] md:px-[40px] md:py-[44px]">
    <div class="absolute inset-0">${photoHtml(banner, 'w-full h-full object-cover')}</div>
    ${body ? '<div class="absolute inset-0" style="background:linear-gradient(180deg,rgba(20,17,15,.15),rgba(20,17,15,.62))"></div>' : ''}
    <div class="hero-content relative z-10 flex flex-col w-full ${ITEMS[align]} ${TEXT_ALIGN[align]}">${body}</div>
  </div>
</div>`;
};

/**
 * Color — no photo, so the banner is the storefront color itself, at one of two
 * weights: the full color with white text on it, or the same color at 10% over
 * white.
 */
const colorBanner = (banner, theme, font, fill) => {
  const { surface, background } = fillSurface(fill, theme);
  const align = alignOf(banner.contentAlignHorizontal);

  const body = join([
    eyebrowHtml(banner.eyebrow, surface, 'mb-[18px]'),
    headlineHtml(banner.title, {
      size: 'clamp(28px,4.2vw,48px)',
      color: surface.title,
      gap: '!mb-[14px]',
      font,
      fullWidth: true,
    }),
    leadHtml(banner.subtitle, {
      size: '17px',
      color: surface.lead,
      maxWidth: '520px',
      align,
    }),
    buttonsHtml(banner, surface, align),
    trustHtml(banner.trustBadges, surface, align),
  ]);

  return `<div class="relative min-w-0 overflow-hidden">
  <div class="main-banner-div hero-color flex flex-col justify-center ${ITEMS[align]} min-h-[350px] md:min-h-[350px] px-[24px] py-[32px] md:px-[40px] md:py-[48px]" style="background:${background}">
    <div class="hero-content flex flex-col w-full ${ITEMS[align]} ${TEXT_ALIGN[align]}">${body}</div>
  </div>
</div>`;
};

/* The only player a video banner may frame. `videoUrl` is saved data, so
   anything else is refused rather than handed to an iframe. YouTube only —
   the section autoplays muted on a loop, which is YouTube's embed behaviour.
   Same list as the designer's copy of this file
   (shopdot-retailer-portal GrapesEditor/bannerMarkup.js). */
const VIDEO_EMBEDS = [
  { provider: 'youtube', hosts: ['youtube.com', 'www.youtube.com'], path: /^\/embed\/([A-Za-z0-9_-]{6,})\/?$/ },
  { provider: 'youtube', hosts: ['youtube-nocookie.com', 'www.youtube-nocookie.com'], path: /^\/embed\/([A-Za-z0-9_-]{6,})\/?$/ },
];

/**
 * Parse a YouTube embed URL. Returns `{ provider, id, url }`, where `url` is the
 * parsed URL (query kept), or null when it is not an allowed player.
 */
export const parseVideoEmbed = (value) => {
  let url;
  try {
    url = new URL(String(value || '').trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  const host = url.hostname.toLowerCase();
  for (const { provider, hosts, path } of VIDEO_EMBEDS) {
    const match = hosts.includes(host) && url.pathname.match(path);
    if (match) {
      url.protocol = 'https:';
      return { provider, id: match[1], url };
    }
  }
  return null;
};

/**
 * Video — plays inline on a continuous muted loop. The player's own controls
 * are off and it takes no clicks; the one control is a play/pause button,
 * bottom right, added by `attachVideoControls` (videoControls.js), which also
 * covers the player while YouTube shows its start overlay. The band is 350px
 * tall and the whole video fits inside it, with black bars at the sides. An
 * empty or disallowed URL yields '' so the caller drops the section.
 */
const videoBanner = (banner, embedUrl) => {
  const embed = parseVideoEmbed(embedUrl);
  if (!embed) return '';
  return `<div class="relative min-w-0 overflow-hidden">
  <div class="main-banner-div hero-video relative w-full h-[350px] md:h-[350px] overflow-hidden bg-black">
    <iframe
      src="${escapeHtml(embed.url.toString())}"
      class="absolute inset-0 w-full h-full border-0 z-[1]"
      style="pointer-events:none;"
      frameborder="0"
      allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
      allowfullscreen
      title="Embedded Video"
    ></iframe>
  </div>
</div>`;
};

/**
 * Build a banner's markup from its stored values. Returns '' for a video
 * banner with no playable URL.
 *
 * @param {object} banner                one `mainBanner` / `banners[n]` config
 * @param {object} options
 * @param {string} options.themeColor    the Style tab's Storefront color
 * @param {string} options.headingFont   the Style tab's Storefront style font
 * @param {string} options.videoEmbedUrl autoplay embed URL for a video banner
 * @param {boolean} options.isMain       the pinned Main banner (always Color block)
 */
export const buildBannerHtml = (banner = {}, options = {}) => {
  const { themeColor, videoEmbedUrl, headingFont, isMain = false } = options;
  const theme = normalizeTheme(themeColor);
  const layout = banner.bannerStyle;
  const fill = resolveBannerFill(banner.bannerFill, theme, isMain);

  if (layout === 'split') return splitBanner(banner, theme, headingFont, fill);
  if (layout === 'color') return colorBanner(banner, theme, headingFont, fill);
  if (layout === 'video') return videoBanner(banner, videoEmbedUrl || banner.videoUrl);
  return imageBanner(banner, theme, headingFont);
};

export default buildBannerHtml;
